import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  DollarSign, 
  Activity, 
  Sparkles, 
  AlertTriangle, 
  Percent, 
  Briefcase, 
  Calendar, 
  ArrowUpRight, 
  Cpu, 
  Layers, 
  RefreshCw, 
  Download,
  Info,
  CheckCircle2,
  HelpCircle,
  TrendingDown
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  LineChart, 
  Line,
  AreaChart,
  Area
} from 'recharts';
import { useAuth } from '../AuthContext';
import { useSavedSolicitations } from '../SavedSolicitationsContext';
import ReactMarkdown from 'react-markdown';

interface AreaAllocation {
  area: string;
  amount: number;
  percentage: number;
  description: string;
}

interface CategoryAllocation {
  category: string;
  amount: number;
  percentage: number;
  description: string;
}

interface HistoricalDataPoint {
  year: string;
  Education: number;
  Healthcare: number;
  Transportation: number;
  PublicSafety: number;
  Tech: number;
  Total: number;
}

interface YearProjection {
  year: string;
  amount: number;
  growthRate: number;
}

interface ScenarioProjection {
  name: string;
  label: string;
  yearProjections: YearProjection[];
  summary: string;
}

interface BudgetAnalysisData {
  stateCode: string;
  stateName: string;
  totalSpending: number;
  byArea: AreaAllocation[];
  byCategory: CategoryAllocation[];
  historicalData: HistoricalDataPoint[];
  scenarios: ScenarioProjection[];
  cagr: number;
  predictedPeakYear: string;
  riskFactors: string[];
  aiInsight: string;
  isFallback?: boolean;
}

const STATES_LIST = [
  { name: 'Alabama', code: 'AL' }, { name: 'Alaska', code: 'AK' }, { name: 'Arizona', code: 'AZ' },
  { name: 'Arkansas', code: 'AR' }, { name: 'California', code: 'CA' }, { name: 'Colorado', code: 'CO' },
  { name: 'Connecticut', code: 'CT' }, { name: 'Delaware', code: 'DE' }, { name: 'Florida', code: 'FL' },
  { name: 'Georgia', code: 'GA' }, { name: 'Hawaii', code: 'HI' }, { name: 'Idaho', code: 'ID' },
  { name: 'Illinois', code: 'IL' }, { name: 'Indiana', code: 'IN' }, { name: 'Iowa', code: 'IA' },
  { name: 'Kansas', code: 'KS' }, { name: 'Kentucky', code: 'KY' }, { name: 'Louisiana', code: 'LA' },
  { name: 'Maine', code: 'ME' }, { name: 'Maryland', code: 'MD' }, { name: 'Massachusetts', code: 'MA' },
  { name: 'Michigan', code: 'MI' }, { name: 'Minnesota', code: 'MN' }, { name: 'Mississippi', code: 'MS' },
  { name: 'Missouri', code: 'MO' }, { name: 'Montana', code: 'MT' }, { name: 'Nebraska', code: 'NE' },
  { name: 'Nevada', code: 'NV' }, { name: 'New Hampshire', code: 'NH' }, { name: 'New Jersey', code: 'NJ' },
  { name: 'New Mexico', code: 'NM' }, { name: 'New York', code: 'NY' }, { name: 'North Carolina', code: 'NC' },
  { name: 'North Dakota', code: 'ND' }, { name: 'Ohio', code: 'OH' }, { name: 'Oklahoma', code: 'OK' },
  { name: 'Oregon', code: 'OR' }, { name: 'Pennsylvania', code: 'PA' }, { name: 'Rhode Island', code: 'RI' },
  { name: 'South Carolina', code: 'SC' }, { name: 'South Dakota', code: 'SD' }, { name: 'Tennessee', code: 'TN' },
  { name: 'Texas', code: 'TX' }, { name: 'Utah', code: 'UT' }, { name: 'Vermont', code: 'VT' },
  { name: 'Virginia', code: 'VA' }, { name: 'Washington', code: 'WA' }, { name: 'West Virginia', code: 'WV' },
  { name: 'Wisconsin', code: 'WI' }, { name: 'Wyoming', code: 'WY' }
];

const CHART_COLORS = [
  '#4F46E5', // Indigo 600
  '#06B6D4', // Cyan 500
  '#10B981', // Emerald 500
  '#F59E0B', // Amber 500
  '#EF4444', // Red 500
  '#8B5CF6', // Violet 500
  '#EC4899', // Pink 500
  '#64748B'  // Slate 500
];

export const StateBudgetView = () => {
  const { vendorProfile } = useAuth();
  const { addToast } = useSavedSolicitations();
  
  // Set default state to active registered state from vendor profile, or California as fallback
  const defaultStateCode = vendorProfile?.businessRegisteredState || 'CA';
  const defaultStateObj = STATES_LIST.find(s => s.code.toUpperCase() === defaultStateCode.toUpperCase()) || STATES_LIST[4]; // California index 4
  
  const [selectedState, setSelectedState] = useState(defaultStateObj);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [budgetData, setBudgetData] = useState<BudgetAnalysisData | null>(null);
  const [activeTab, setActiveTab] = useState<'allocations' | 'projections' | 'trends' | 'ai-insights'>('allocations');
  const [selectedScenario, setSelectedScenario] = useState<string>('conservative');

  const fetchBudgetData = async (stateCode: string, stateName: string, forceRefresh = false) => {
    if (forceRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const response = await fetch('/api/budget/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stateCode, stateName })
      });

      if (!response.ok) {
        throw new Error('Server returned an error generating budget analysis');
      }

      const data = await response.json();
      setBudgetData(data);
      if (addToast) {
        addToast({
          title: 'Budget Profile Synchronized',
          message: `Successfully loaded spend profile and predictive analytics for ${stateName}.`,
          type: 'success'
        });
      }
    } catch (err) {
      console.error('Failed to load budget profile:', err);
      if (addToast) {
        addToast({
          title: 'Budget Sync Failed',
          message: 'Live fiscal analysis could not be loaded. Please verify the server connection and try again.',
          type: 'danger'
        });
      }
      throw err;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBudgetData(selectedState.code, selectedState.name);
  }, [selectedState]);

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const matched = STATES_LIST.find(s => s.code === e.target.value);
    if (matched) {
      setSelectedState(matched);
    }
  };

  if (loading && !refreshing) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center p-8 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-600 font-medium">Synthesizing State Allocation Profiles...</p>
        <p className="text-xs text-gray-400 mt-1">Grounded in fiscal records, legislative files, and predictive CAGR models</p>
      </div>
    );
  }

  // Combine scenarios yearProjections for side-by-side comparison
  const getScenariosComparisonData = () => {
    if (!budgetData) return [];
    const years = ["2027", "2028", "2029", "2030"];
    return years.map(year => {
      const point: any = { year };
      budgetData.scenarios.forEach(sc => {
        const proj = sc.yearProjections.find(p => p.year === year);
        if (proj) {
          // Keep label readable
          const key = sc.label.split(' ')[0]; // e.g. "Conservative", "Optimistic"
          point[key] = proj.amount;
        }
      });
      return point;
    });
  };

  const getActiveScenarioData = () => {
    if (!budgetData) return null;
    return budgetData.scenarios.find(s => s.name === selectedScenario) || budgetData.scenarios[0];
  };

  const activeScenario = getActiveScenarioData();
  const comparisonData = getScenariosComparisonData();

  return (
    <div className="space-y-6">
      {/* Dynamic Header Board */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="bg-indigo-50 text-indigo-700 text-xs font-bold px-2.5 py-1 rounded-lg border border-indigo-100 flex items-center gap-1.5">
              <Cpu className="w-3.5 h-3.5 animate-pulse text-indigo-600" />
              State Allocations & AI Forecasting
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-950 tracking-tight">
            Fiscal Spending Profile & Forecast
          </h1>
          <p className="text-sm text-gray-500">
            Detailing spending structures, multi-variant projections, structural trends, and procurement fit analytics.
          </p>
        </div>

        {/* State Selection and Refresh Widget */}
        <div className="flex items-center gap-3 self-start md:self-center">
          <div className="relative">
            <select
              value={selectedState.code}
              onChange={handleStateChange}
              className="appearance-none bg-gray-50 hover:bg-gray-100 border border-gray-300 text-gray-900 text-sm font-semibold rounded-xl focus:ring-indigo-500 focus:border-indigo-500 block w-48 p-3 pr-10 outline-none transition-all cursor-pointer"
            >
              {STATES_LIST.map(st => (
                <option key={st.code} value={st.code}>
                  {st.name} ({st.code})
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-gray-500">
              <ChevronDownIcon />
            </div>
          </div>

          <button
            onClick={() => fetchBudgetData(selectedState.code, selectedState.name, true)}
            disabled={refreshing}
            className="p-3 bg-white hover:bg-gray-50 border border-gray-300 rounded-xl text-gray-700 transition-all flex items-center justify-center disabled:opacity-50"
            title="Recalculate Budget Forecasting"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin text-indigo-600' : ''}`} />
          </button>
        </div>
      </div>

      {budgetData && (
        <>
          {/* Key Metric Bento Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-2">
              <div className="flex justify-between items-start">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Total Active Budget</span>
                <div className="p-2 bg-indigo-50 rounded-xl text-indigo-600">
                  <DollarSign className="w-5 h-5" />
                </div>
              </div>
              <div>
                <p className="text-2xl font-black text-gray-900 font-mono">
                  ${budgetData.totalSpending.toFixed(1)}B
                </p>
                <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1 mt-1">
                  <TrendingUp className="w-3 h-3" /> +{budgetData.cagr}% Historical CAGR
                </p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-2">
              <div className="flex justify-between items-start">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Procurement Allocation</span>
                <div className="p-2 bg-cyan-50 rounded-xl text-cyan-600">
                  <Briefcase className="w-5 h-5" />
                </div>
              </div>
              <div>
                <p className="text-2xl font-black text-gray-900 font-mono">
                  ${(budgetData.totalSpending * 0.18).toFixed(1)}B
                </p>
                <p className="text-xs text-gray-500 font-medium mt-1">
                  ~18.0% of total outlays allocated to vendors
                </p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-2">
              <div className="flex justify-between items-start">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">4-Year Projection (Conservative)</span>
                <div className="p-2 bg-emerald-50 rounded-xl text-emerald-600">
                  <TrendingUp className="w-5 h-5" />
                </div>
              </div>
              <div>
                <p className="text-2xl font-black text-gray-900 font-mono">
                  ${budgetData.scenarios[0].yearProjections[3].amount.toFixed(1)}B
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Target ceiling projection by year 2030
                </p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-2">
              <div className="flex justify-between items-start">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Critical Risk Indicator</span>
                <div className="p-2 bg-amber-50 rounded-xl text-amber-600">
                  <AlertTriangle className="w-5 h-5 animate-pulse" />
                </div>
              </div>
              <div>
                <p className="text-lg font-bold text-gray-800 leading-tight">
                  High Pension Load
                </p>
                <p className="text-xs text-gray-500 mt-1 truncate" title={budgetData.riskFactors[0]}>
                  {budgetData.riskFactors[0]}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Tab rail */}
          <div className="flex border-b border-gray-200 bg-white px-2 pt-2 rounded-2xl border">
            {(['allocations', 'projections', 'trends', 'ai-insights'] as const).map(tabId => {
              const labels = {
                'allocations': 'Spending Allocation Profile',
                'projections': '4 Projections & Analysis',
                'trends': 'Forecasting & Prediction Trends',
                'ai-insights': 'AI Fiscal Insights'
              };
              const icons = {
                'allocations': Layers,
                'projections': Calendar,
                'trends': Activity,
                'ai-insights': Sparkles
              };
              const Icon = icons[tabId];
              const isActive = activeTab === tabId;
              return (
                <button
                  key={tabId}
                  onClick={() => setActiveTab(tabId)}
                  className={`flex items-center gap-2 px-5 py-4 border-b-2 text-sm font-semibold transition-all cursor-pointer ${
                    isActive 
                      ? 'border-indigo-600 text-indigo-700' 
                      : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-250'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                  {labels[tabId]}
                </button>
              );
            })}
          </div>

          {/* Tab Content Rendering */}
          <div className="space-y-6">
            {/* Tab 1: Allocation Profile */}
            {activeTab === 'allocations' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: Department Spending Pie Chart */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-6 flex flex-col justify-between">
                  <div className="space-y-1">
                    <h3 className="font-bold text-gray-900 flex items-center gap-2 text-base">
                      <span className="w-2 h-4 bg-indigo-600 rounded" />
                      Spending Outlays by Major Department / Area
                    </h3>
                    <p className="text-xs text-gray-500">
                      Distribution of state outlays across primary statutory service fields.
                    </p>
                  </div>

                  <div className="h-64 flex items-center justify-center relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={budgetData.byArea}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={3}
                          dataKey="amount"
                          nameKey="area"
                        >
                          {budgetData.byArea.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value: any) => [`$${value}B`, 'Spending']}
                          contentStyle={{ backgroundColor: '#1E293B', borderRadius: '12px', border: 'none', color: '#fff' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute flex flex-col items-center">
                      <span className="text-xs text-gray-400 font-bold uppercase tracking-wide">Total Spent</span>
                      <span className="text-2xl font-extrabold text-gray-900 font-mono">${budgetData.totalSpending.toFixed(1)}B</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    {budgetData.byArea.map((entry, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-md shrink-0" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                        <span className="text-gray-600 font-medium truncate" title={entry.area}>
                          {entry.area} ({entry.percentage}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Category Objects Bar Chart */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-6 flex flex-col justify-between">
                  <div className="space-y-1">
                    <h3 className="font-bold text-gray-900 flex items-center gap-2 text-base">
                      <span className="w-2 h-4 bg-cyan-600 rounded" />
                      Outlays by Category & Object of Expenditure
                    </h3>
                    <p className="text-xs text-gray-500">
                      Breakdown showing the operational mechanisms of expenditure.
                    </p>
                  </div>

                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={budgetData.byCategory} layout="vertical" margin={{ left: 5, right: 15, top: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                        <XAxis type="number" stroke="#94A3B8" fontSize={10} unit="B" />
                        <YAxis dataKey="category" type="category" width={110} stroke="#94A3B8" fontSize={9} />
                        <Tooltip 
                          formatter={(value: any) => [`$${value}B`, 'Expenditure']}
                          contentStyle={{ backgroundColor: '#1E293B', borderRadius: '12px', border: 'none', color: '#fff' }}
                        />
                        <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                          {budgetData.byCategory.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[(index + 3) % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="text-[10px] text-gray-400 flex items-center gap-1 bg-gray-50 p-2.5 rounded-xl border border-gray-150">
                    <Info className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                    <span><strong>Procurement Note</strong>: <em>Contractual Services</em> represents direct private-sector bidding opportunities.</span>
                  </div>
                </div>

                {/* Detailed Table Matrix below */}
                <div className="col-span-1 lg:col-span-2 bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
                  <h3 className="font-bold text-gray-900 text-sm">Detailed Allocation Breakdown Matrix</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50/50 text-gray-400 font-bold uppercase tracking-wider">
                          <th className="py-3 px-4">Allocation Subject Area / Category</th>
                          <th className="py-3 px-4 text-right">Amount (Billion)</th>
                          <th className="py-3 px-4 text-center">Share</th>
                          <th className="py-3 px-4">Functional Mandate & Purpose</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 font-medium text-gray-700">
                        {budgetData.byArea.map((item, idx) => (
                          <tr key={`area-${idx}`} className="hover:bg-gray-50/40">
                            <td className="py-3 px-4 font-bold text-gray-950 flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
                              {item.area}
                            </td>
                            <td className="py-3 px-4 text-right font-mono font-bold text-gray-900">${item.amount.toFixed(2)}B</td>
                            <td className="py-3 px-4 text-center font-mono">
                              <span className="bg-indigo-50 text-indigo-700 text-[10px] font-extrabold px-2 py-0.5 rounded border border-indigo-100">
                                {item.percentage}%
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-500 leading-relaxed font-normal">{item.description}</td>
                          </tr>
                        ))}
                        {budgetData.byCategory.map((item, idx) => (
                          <tr key={`cat-${idx}`} className="hover:bg-gray-50/40 bg-gray-50/10">
                            <td className="py-3 px-4 font-bold text-indigo-950 flex items-center gap-2 pl-4 border-l-2 border-indigo-500">
                              {item.category}
                            </td>
                            <td className="py-3 px-4 text-right font-mono font-bold text-indigo-900">${item.amount.toFixed(2)}B</td>
                            <td className="py-3 px-4 text-center font-mono">
                              <span className="bg-cyan-50 text-cyan-700 text-[10px] font-extrabold px-2 py-0.5 rounded border border-cyan-100">
                                {item.percentage}%
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-500 leading-relaxed font-normal">{item.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 2: 4 Projections & Analysis */}
            {activeTab === 'projections' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: Interactive Scenario Selector & Projections List */}
                <div className="lg:col-span-1 bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4 flex flex-col justify-between">
                  <div className="space-y-1">
                    <h3 className="font-bold text-gray-900 text-base">4 Projection Horizons</h3>
                    <p className="text-xs text-gray-500">
                      Explore dynamic spending pathways mapped across distinct economic scenarios.
                    </p>
                  </div>

                  <div className="space-y-2.5">
                    {budgetData.scenarios.map(sc => {
                      const isSelected = selectedScenario === sc.name;
                      return (
                        <button
                          key={sc.name}
                          onClick={() => setSelectedScenario(sc.name)}
                          className={`w-full text-left p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col gap-1.5 ${
                            isSelected 
                              ? 'bg-indigo-50/50 border-indigo-300 shadow-sm' 
                              : 'bg-white hover:bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex justify-between items-center w-full">
                            <span className={`text-xs font-bold ${isSelected ? 'text-indigo-800 font-extrabold' : 'text-gray-700'}`}>
                              {sc.label}
                            </span>
                            <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded border ${
                              sc.name === 'optimistic' ? 'bg-emerald-50 border-emerald-100 text-emerald-700' :
                              sc.name === 'pessimistic' ? 'bg-rose-50 border-rose-100 text-rose-700' :
                              sc.name === 'strategic' ? 'bg-cyan-50 border-cyan-100 text-cyan-700' :
                              'bg-gray-50 border-gray-200 text-gray-600'
                            }`}>
                              {sc.name.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-500 leading-snug line-clamp-2">
                            {sc.summary}
                          </p>
                        </button>
                      );
                    })}
                  </div>

                  {activeScenario && (
                    <div className="bg-indigo-950 text-indigo-100 p-4 rounded-xl space-y-1 border border-indigo-900 shadow-sm">
                      <span className="text-[9px] font-black tracking-widest uppercase text-indigo-300">Selected Path Metrics</span>
                      <p className="text-xs leading-relaxed font-medium">
                        Expected 2030 Ceiling: <strong className="text-white font-mono font-bold text-sm">${activeScenario.yearProjections[3].amount.toFixed(1)} Billion</strong>
                      </p>
                    </div>
                  )}
                </div>

                {/* Right: Line Chart Mapping Scenarios side-by-side */}
                <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-6 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <h3 className="font-bold text-gray-900 text-base">Interactive Scenario Modeling (FY 2027 - 2030)</h3>
                      <p className="text-xs text-gray-500">
                        Visualizing variance between Conservative, Optimistic, Pessimistic, and Strategic paths.
                      </p>
                    </div>
                  </div>

                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={comparisonData} margin={{ left: 5, right: 10, top: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                        <XAxis dataKey="year" stroke="#94A3B8" fontSize={11} />
                        <YAxis stroke="#94A3B8" fontSize={11} unit="B" />
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', borderRadius: '12px', border: 'none', color: '#fff' }} />
                        <Legend verticalAlign="top" height={36} iconType="circle" />
                        <Line type="monotone" dataKey="Conservative" stroke="#4F46E5" strokeWidth={3} dot={{ r: 5 }} activeDot={{ r: 7 }} />
                        <Line type="monotone" dataKey="Optimistic" stroke="#10B981" strokeWidth={3} strokeDasharray="3 3" dot={{ r: 5 }} />
                        <Line type="monotone" dataKey="Pessimistic" stroke="#EF4444" strokeWidth={3} strokeDasharray="5 5" dot={{ r: 5 }} />
                        <Line type="monotone" dataKey="Strategic" stroke="#06B6D4" strokeWidth={3} dot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {activeScenario && (
                    <div className="grid grid-cols-4 gap-4 pt-4 border-t border-gray-100 text-center">
                      {activeScenario.yearProjections.map((p, i) => (
                        <div key={i} className="space-y-0.5">
                          <p className="text-[10px] text-gray-400 font-extrabold uppercase">FY {p.year}</p>
                          <p className="text-base font-extrabold text-gray-900 font-mono">${p.amount.toFixed(1)}B</p>
                          <p className="text-[10px] text-emerald-600 font-semibold">+{p.growthRate}% yr/yr</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tab 3: Forecasting & Predictions */}
            {activeTab === 'trends' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Historical Area Chart */}
                <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-6">
                  <div className="space-y-1">
                    <h3 className="font-bold text-gray-900 text-base">Historical Budget Trend Matrix (2023 - 2026)</h3>
                    <p className="text-xs text-gray-500">
                      Tracking sector growth velocities leading up to the current fiscal cycle.
                    </p>
                  </div>

                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={budgetData.historicalData} margin={{ left: 5, right: 10, top: 10, bottom: 5 }}>
                        <defs>
                          <linearGradient id="colorEducation" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorHealthcare" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#06B6D4" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                        <XAxis dataKey="year" stroke="#94A3B8" fontSize={11} />
                        <YAxis stroke="#94A3B8" fontSize={11} unit="B" />
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', borderRadius: '12px', border: 'none', color: '#fff' }} />
                        <Legend verticalAlign="top" height={36} />
                        <Area type="monotone" name="Education" dataKey="Education" stroke="#4F46E5" fillOpacity={1} fill="url(#colorEducation)" />
                        <Area type="monotone" name="Healthcare" dataKey="Healthcare" stroke="#06B6D4" fillOpacity={1} fill="url(#colorHealthcare)" />
                        <Area type="monotone" name="Transportation" dataKey="Transportation" stroke="#10B981" fillOpacity={0} />
                        <Area type="monotone" name="Public Safety" dataKey="PublicSafety" stroke="#EF4444" fillOpacity={0} />
                        <Area type="monotone" name="Commerce & Tech" dataKey="Tech" stroke="#8B5CF6" fillOpacity={0} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Predictive Analytics Bento Column */}
                <div className="space-y-4">
                  {/* Peak Predictor Card */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="p-1.5 bg-indigo-50 text-indigo-700 rounded-lg">
                        <Activity className="w-4 h-4" />
                      </span>
                      <h4 className="font-bold text-gray-900 text-sm">Predictive Index Modeling</h4>
                    </div>

                    <div className="space-y-4 pt-2">
                      <div className="flex justify-between items-center border-b border-gray-100 pb-2.5">
                        <span className="text-xs text-gray-500 font-medium">Compound Est. Growth</span>
                        <span className="text-sm font-bold text-gray-900 font-mono flex items-center gap-0.5">
                          <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                          {budgetData.cagr}% CAGR
                        </span>
                      </div>
                      <div className="flex justify-between items-center border-b border-gray-100 pb-2.5">
                        <span className="text-xs text-gray-500 font-medium">Fiscal Peak Exposure</span>
                        <span className="text-sm font-bold text-indigo-600 font-mono">
                          FY {budgetData.predictedPeakYear}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500 font-medium">Stress Test Resilience</span>
                        <span className="text-xs font-black uppercase text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded border border-emerald-100">
                          Stable / High
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Fiscal Risk Registry */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="p-1.5 bg-amber-50 text-amber-700 rounded-lg">
                        <AlertTriangle className="w-4 h-4" />
                      </span>
                      <h4 className="font-bold text-gray-900 text-sm">Fiscal Stress Risks</h4>
                    </div>
                    
                    <div className="space-y-2.5 pt-1">
                      {budgetData.riskFactors.map((risk, index) => (
                        <div key={index} className="flex gap-2.5 items-start text-xs leading-relaxed">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0 mt-1.5" />
                          <p className="text-gray-600">{risk}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 4: AI Insights Report */}
            {activeTab === 'ai-insights' && (
              <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm space-y-6">
                <div className="flex justify-between items-start border-b border-gray-100 pb-5">
                  <div className="space-y-1">
                    <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-indigo-600 animate-pulse" />
                      AI Fiscal Intelligence Summary
                    </h3>
                    <p className="text-xs text-gray-500">
                      Smart strategic summary outlining procurement pipeline matches, regulatory programs, and stress analytics.
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      if (addToast) {
                        addToast({
                          title: 'Export Complete',
                          message: `Downloaded budget analysis package for ${selectedState.name}.`,
                          type: 'success'
                        });
                      }
                      
                      // Construct beautiful text output
                      const text = `===========================================================
STATE BUDGET SPENDING & ALLOCATION PROFILE: ${selectedState.name.toUpperCase()} (${selectedState.code})
===========================================================
Generated: ${new Date().toLocaleDateString()}
Total Spending base: $${budgetData.totalSpending.toFixed(2)} Billion
Historical Growth (CAGR): ${budgetData.cagr}%

-----------------------------------------------------------
1. BUDGET ALLOCATION BY AREA / DEPARTMENT
-----------------------------------------------------------
${budgetData.byArea.map(a => `- ${a.area}: $${a.amount.toFixed(2)}B (${a.percentage}%)
  Purpose: ${a.description}`).join('\n')}

-----------------------------------------------------------
2. OPERATIONAL EXPENDITURE BY OBJECT / CATEGORY
-----------------------------------------------------------
${budgetData.byCategory.map(c => `- ${c.category}: $${c.amount.toFixed(2)}B (${c.percentage}%)
  Purpose: ${c.description}`).join('\n')}

-----------------------------------------------------------
3. FUTURE PROJECTION SCENARIOS (4 HORIZONS)
-----------------------------------------------------------
${budgetData.scenarios.map(sc => `[${sc.label}]
Summary: ${sc.summary}
Projections:
${sc.yearProjections.map(yp => `  * FY ${yp.year}: $${yp.amount.toFixed(2)}B (+${yp.growthRate}% Growth)`).join('\n')}
`).join('\n')}

-----------------------------------------------------------
4. FISCAL RISK FACTORS & METRICS
-----------------------------------------------------------
- Predicted Fiscal Peak Exposure: FY ${budgetData.predictedPeakYear}
${budgetData.riskFactors.map(rf => `- ${rf}`).join('\n')}

-----------------------------------------------------------
5. AI EXECUTIVE INSIGHTS
-----------------------------------------------------------
${budgetData.aiInsight.replace(/###/g, '').replace(/####/g, '').replace(/\*\*/g, '')}

===========================================================
End of Fiscal Spend Profile Report. Generated by GovBid AI Studio.
===========================================================`;

                      const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `${selectedState.code}_GovBid_Budget_Analysis.txt`);
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 font-semibold text-xs rounded-xl transition-all cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" /> Export Report Brief
                  </button>
                </div>

                <div className="prose prose-indigo max-w-none text-sm text-gray-700 leading-relaxed space-y-4">
                  <div className="markdown-body bg-slate-50/50 border border-gray-150 p-6 rounded-2xl font-normal">
                    <ReactMarkdown>{budgetData.aiInsight}</ReactMarkdown>
                  </div>
                </div>

                <div className="bg-indigo-50 border border-indigo-150 p-4.5 rounded-2xl flex gap-3.5 items-start text-xs leading-relaxed text-indigo-950">
                  <Sparkles className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5 animate-pulse" />
                  <div>
                    <h4 className="font-bold text-indigo-900">Procurement Integration Opportunity:</h4>
                    <p className="mt-1 font-medium text-indigo-800">
                      Based on **{selectedState.name}**'s heavy contractual outlay budget of **${(budgetData.totalSpending * 0.18).toFixed(1)} Billion**, 
                      our match algorithms recommend focusing bid proposals on NAICS sectors and state preference programs 
                      (such as small business set-asides) where procurement speeds are projected to accelerate in early **{budgetData.predictedPeakYear}**.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Simple Arrow Down Icon replacement for Lucide icon
const ChevronDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
  </svg>
);
